import React, { useEffect, useCallback, useRef, useState } from 'react';
import { API_URL } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { loginStart, loginSuccess, loginFailure } from '../store/slices/authSlice';
import {
  Container,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Login as LoginIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Google Identity Services types
declare global {
  interface Window {
    google: any;
  }
}

// Custom Google Sign-In Button Component
const CustomGoogleSignInButton: React.FC<{ 
  onClick: () => void; 
  loading: boolean; 
  disabled: boolean; 
}> = ({ onClick, loading, disabled }) => {
  return (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      variant="contained"
      size="large"
      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
      sx={{
        width: '100%',
        minHeight: '48px',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        fontWeight: 600,
        fontSize: '1.1rem',
        borderRadius: '8px',
        padding: '16px 24px',
        border: 'none',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        transition: 'all 0.2s ease-in-out',
        textTransform: 'none',
        '&:hover': {
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          transform: 'translateY(-1px) scale(1.02)',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 10px 10px -5px rgb(0 0 0 / 0.04)',
        },
        '&:active': {
          transform: 'translateY(0) scale(0.98)',
        },
        '&:disabled': {
          background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
          transform: 'none',
          boxShadow: 'none',
          cursor: 'not-allowed',
        },
      }}
    >
      {loading ? 'Signing you in...' : 'Sign in with Google'}
    </Button>
  );
};

const Login: React.FC = () => {
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  const handleGoogleSignIn = useCallback(async (response: any) => {
    setError('');
    setLoading(true);
    dispatch(loginStart());

    try {
      const tokenResponse = await axios.post(API_URL + '/token', {
        credential: response.credential,
      });

      const { access_token } = tokenResponse.data;
      dispatch(loginSuccess(access_token));
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Google sign-in failed';
      setError(errorMessage);
      dispatch(loginFailure(errorMessage));
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate]);

  const handleCustomButtonClick = useCallback(() => {
    if (window.google && googleReady) {
      // Trigger Google One Tap or redirect flow
      window.google.accounts.id.prompt();
    } else {
      setError('Google Sign-In is not ready. Please try again.');
    }
  }, [googleReady]);

  const initializeGoogleSignIn = useCallback(() => {
    if (window.google) {
      try {
        console.log('Initializing Google OAuth Client ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID);
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleSignIn,
          auto_select: false,
          cancel_on_tap_outside: false,
        });
        
        setGoogleReady(true);
        retryCountRef.current = 0;
      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current++;
          setTimeout(initializeGoogleSignIn, 1000 * retryCountRef.current);
        } else {
          setError('Failed to initialize Google Sign-In. Please refresh the page.');
        }
      }
    }
  }, [handleGoogleSignIn]);

  useEffect(() => {
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      // Add a small delay to ensure Google's script is fully loaded
      setTimeout(initializeGoogleSignIn, 100);
    };

    script.onerror = () => {
      console.error('Failed to load Google Identity Services script');
      setError('Failed to load Google Sign-In. Please check your internet connection and try again.');
    };

    return () => {
      // Clean up
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [initializeGoogleSignIn]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              p: 4,
              textAlign: 'center',
            }}
          >
            <LoginIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
              Welcome Back
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Sign in with your Google account to continue
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <CustomGoogleSignInButton
                onClick={handleCustomButtonClick}
                loading={loading}
                disabled={!googleReady}
              />
              
              {!googleReady && !error && (
                <Box sx={{ mt: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Loading Google Sign-In...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                By signing in, you agree to our terms of service and privacy policy.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            © 2025 Meeting Transcription App. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login; 
